<template>
  <div class="asofarma-dashboard container-fluid d-flex flex-column align-items-center mt-5 pb-5 mb-5">
    
    <!-- === KPIs GLOBALES === -->
    <div class="row w-100 justify-content-center mb-5">
      <div class="col-auto">
        <div class="kpi-card text-center mx-2 shadow-sm transition-hover">
          <p class="kpi-title">Tarjetas en Pool</p> 
          <p class="kpi-value text-dark-green">{{ tarjetasEnPool }}</p>
        </div>
      </div>
      <div class="col-auto">
        <div class="kpi-card text-center mx-2 shadow-sm transition-hover">
          <p class="kpi-title">Faltantes por Activar</p>
          <p class="kpi-value" :style="{ color: colorFaltantes }">{{ faltantes }}</p>
        </div>
      </div>
    </div>

    <!-- === TABS DE NAVEGACIÓN === -->
    <div class="row w-100 justify-content-center mb-5">
      <div class="col-auto text-center w-100">
        <div class="custom-toggle shadow-sm d-inline-flex align-items-center mx-auto">
          <div class="toggle-option d-flex align-items-center justify-content-center" 
               :class="{ active: currentTab === 'alta' }" @click="currentTab = 'alta'">
            <span class="material-symbols-outlined me-2 fs-5">credit_card</span> Alta de Tarjetas
          </div>
          <div class="toggle-option d-flex align-items-center justify-content-center" 
               :class="{ active: currentTab === 'activar' }" @click="currentTab = 'activar'">
            <span class="material-symbols-outlined me-2 fs-5">check_circle</span> Activar Tarjeta
          </div>
        </div>
      </div>
    </div>

    <!-- === TABLAS Y Modales === -->
    <transition name="fade" mode="out-in">
      
      <!-- TABLA DE PACIENTES -->
      <div v-if="currentTab === 'alta'" class="w-100" key="alta">
        <h2 class="main-title mb-4">Datos para Tarjetas ASOFARMA</h2>

        <div class="table-responsive custom-table-wrapper shadow-sm rounded-3">
          <table class="table custom-table text-center align-middle mb-0">
            <thead>
              <tr>
                <th>Nombre(s)</th>
                <th>Apellido(s)</th>
                <th>Fecha de Nacimiento</th> 
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <!-- Cuando no hay datos -->
              <tr v-if="pacientes.length === 0">
                <td colspan="4" class="py-5 text-muted">
                  <span class="material-symbols-outlined fs-1 mb-2">inbox</span>
                  <p class="mb-0 fw-medium">No hay tarjetas pendientes por asignar.</p>
                </td>
              </tr>

              <!-- Fila iterativa de pacientes -->
              <tr v-else v-for="(paciente, index) in pacientes" :key="paciente.id || index" :class="{'striped-row': index % 2 !== 0}">
                <td class="py-3">{{ paciente.nombre }}</td>
                <td>{{ paciente.apellido }}</td>
                <td>{{ paciente.fecha_nacimiento }}</td> 
                <td class="text-center">
                  
                  <span v-if="isRecordProcessed(paciente) || paciente.status === 'activated'" class="badge-status badge-success">
                    Tarjeta Activada
                  </span>
                  
                  <span v-else-if="isRecordFailed(paciente) || paciente.status === 'failed'" class="badge-status badge-success">
                    Registro No Válido
                  </span>
                  
                  <button v-else 
                          class="btn custom-btn-action px-4" 
                          :disabled="loadingRowIndex === index"
                          @click="openModal(paciente, index)">
                    <span v-if="loadingRowIndex === index" class="spinner-border spinner-border-sm me-2"></span>
                    {{ loadingRowIndex === index ? 'Verificando...' : 'Activar Tarjeta' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div class="row w-100 align-items-center mt-4 custom-table-wrapper" v-if="pacientes.length > 0">
          <div class="col-md-6 text-md-start text-center text-muted info-text mb-3 mb-md-0 fw-medium">
            {{ paginationText }}
          </div>

          <div class="col-md-6 d-flex justify-content-md-end justify-content-center">
            <nav aria-label="Navegación de resultados">
              <ul class="pagination mb-0 custom-bs-pagination shadow-sm">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link d-flex align-items-center justify-content-center px-2" @click="prevPage">
                    <span class="material-symbols-outlined fs-6">chevron_left</span>
                  </button>
                </li>

                <li class="page-item" v-for="(page, index) in visiblePages" :key="index" 
                    :class="{ active: currentPage === page, disabled: page === '...' }">
                  <button class="page-link" @click="page !== '...' ? goToPage(page) : null">
                    {{ page }}
                  </button>
                </li>

                <li class="page-item" :class="{ disabled: currentPage === lastPage }">
                  <button class="page-link d-flex align-items-center justify-content-center px-2" @click="nextPage">
                    <span class="material-symbols-outlined fs-6">chevron_right</span>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    
      <!-- PESTAÑA INGRESO DIRECTO DE TARJETA -->
      <div v-else-if="currentTab === 'activar'" class="w-100" key="activar">
        <h2 class="main-title mb-4">Ingresa la Tarjeta Generada por Asofarma</h2>
        
        <div class="direct-activation-container text-center">
          <input type="text" class="form-control custom-input mb-4 mx-auto text-center shadow-sm" 
                 placeholder="Ej. 5601026135421" 
                 v-model="directTab.cardNumber"
                 maxlength="13" minlength="13" inputmode="numeric"
                 @input="directTab.cardNumber = directTab.cardNumber.replace(/[^0-9]/g, '')">
          
          <button class="btn custom-btn-action px-5 shadow-sm transition-hover" 
                  @click="submitDirectActivation" 
                  :disabled="!isValidDirectCard || isProcessingPost">
            <span v-if="isProcessingPost" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isProcessingPost ? 'Procesando...' : 'Registrar Tarjeta' }}
          </button>
        </div>
      </div>
    </transition>

    <!-- === MODAL DE ACTIVACIÓN DE TARJETA === -->
    <transition name="fade-modal">
      <div v-if="showModal" class="custom-modal-overlay" @click.self="closeModal">
        <div class="custom-modal-content text-start">
          
          <button class="close-btn transition-hover" @click="closeModal">
            <span class="material-symbols-outlined fs-3">close</span>
          </button>
          
          <h3 class="modal-title font-amaranth">Datos de Registro</h3>
          <p class="modal-subtitle">Copia e ingresa todos los datos en: <a target="blank" href="https://programaactua.com/registro/" class="text-green">Programa Actua</a></p>
          
          <!-- Checkbox de Concurrencia -->
          <label class="custom-checkbox-label mb-4 d-flex align-items-center">
            <div class="custom-checkbox me-2" :class="{ 'checked': modalData.enableInput }">
              <span v-if="modalData.enableInput" class="material-symbols-outlined fs-6 fw-bold">check</span>
            </div>
            <input type="checkbox" class="d-none" v-model="modalData.enableInput" @change="handleLockToggle">
            <span class="mx-2" v-if="modalData.enableInput">Estás dando de alta esta información</span>
            <span class="mx-2" v-else>Deseas <strong class="text-green">Dar de Alta</strong> esta información?</span>
          </label>

          <!-- Bloques de Información -->
          <h4 class="section-title">Datos Personales</h4>
          <div class="row g-3 form-section">
            <div class="col-md-6" v-for="(value, key) in modalData.personal" :key="key">
              <label class="input-label">{{ key }}</label>
              <div class="copy-input-group">
                <template v-if="key === 'Sexo' && modalData.isGenderNull">
                  <select class="form-control readonly-input w-100" style="cursor: pointer; appearance: auto;" 
                          v-model="modalData.personal[key]" @change="actualizarCurp">
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                  </select>
                </template>
                <template v-else>
                  <input type="text" class="form-control readonly-input" :value="value" readonly>
                  <transition name="fade">
                    <button v-if="modalData.enableInput" class="copy-btn" @click="copyToClipboard(value)" title="Copiar">
                      <span class="material-symbols-outlined fs-5">content_copy</span>
                    </button>
                  </transition>
                </template>
              </div>
            </div>
          </div>

          <h4 class="section-title mt-4">Datos del Médico</h4>
          <div class="row g-3 form-section">
            <div class="col-md-6" v-for="(value, key) in modalData.medico" :key="key">
              <label class="input-label">{{ key }}</label>
              <div class="copy-input-group">
                <input type="text" class="form-control readonly-input" :value="value" readonly>
                <transition name="fade">
                  <button v-if="modalData.enableInput" class="copy-btn" @click="copyToClipboard(value)" title="Copiar">
                    <span class="material-symbols-outlined fs-5">content_copy</span>
                  </button>
                </transition>
              </div>
            </div>
          </div>

          <h4 class="section-title mt-4">Productos y Compra</h4>
          <div class="row g-3 form-section">
            <div class="col-md-6" v-if="modalData.producto && modalData.producto['No. Ticket']">
              <label class="input-label">No. Ticket</label>
              <div class="copy-input-group">
                <input type="text" class="form-control readonly-input" :value="modalData.producto['No. Ticket']" readonly>
                <transition name="fade">
                  <button v-if="modalData.enableInput" class="copy-btn" @click="copyToClipboard(modalData.producto['No. Ticket'])" title="Copiar">
                    <span class="material-symbols-outlined fs-5">content_copy</span>
                  </button>
                </transition>
              </div>
            </div>
            
            <div class="w-100 m-0 p-0" v-if="modalData.producto && modalData.producto['No. Ticket']"></div>

            <template v-for="(value, key) in modalData.producto">
              <div class="col-md-6" v-if="key !== 'No. Ticket'" :key="key">
                <label class="input-label">{{ key }}</label>
                <div class="copy-input-group">
                  <input type="text" class="form-control readonly-input" :value="value" readonly>
                  <transition name="fade">
                    <button v-if="modalData.enableInput" class="copy-btn" @click="copyToClipboard(value)" title="Copiar">
                      <span class="material-symbols-outlined fs-5">content_copy</span>
                    </button>
                  </transition>
                </div>
              </div>
            </template>
          </div>

          <!-- Botones del Modal -->
          <div class="text-center mt-5 pt-4 border-top">
            <h4 class="mb-4 font-weight-bold section-title text-center">Ingresa la Tarjeta Generada por Asofarma</h4>
            <!-- Refactor: Inputs robustos para tarjetas de 13 dígitos -->
            <input type="text" class="form-control custom-input mx-auto text-center mb-4 transition-hover" 
                   v-model="modalData.cardNumber" 
                   :disabled="!modalData.enableInput"
                   maxlength="13" minlength="13" inputmode="numeric"
                   @input="modalData.cardNumber = modalData.cardNumber.replace(/[^0-9]/g, '')">
            
            <div class="d-flex justify-content-center">
              <button class="btn transition-hover mx-2" :class="modalData.enableInput ? 'custom-btn-error' : 'custom-btn-disabled'"
                      :disabled="!modalData.enableInput || isProcessingPost" @click="processRow('failed')">
                No se puede registrar
              </button>
              
              <button class="btn transition-hover mx-2" :class="modalData.enableInput ? 'custom-btn-action' : 'custom-btn-disabled'"
                      :disabled="!modalData.enableInput || !isValidCard || isProcessingPost" @click="processRow('activated')">
                <span v-if="isProcessingPost" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ isProcessingPost ? 'Procesando...' : 'Registrar Tarjeta' }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </transition>

    <!-- === SISTEMA CENTRAL DE NOTIFICACIONES (TOASTS) === -->
    <transition name="toast">
      <div v-if="appToast.show" 
          class="clipboard-toast"
          :class="`toast-${appToast.type}`">
        <span class="material-symbols-outlined fs-5 me-2">
          {{ 
            appToast.type === 'success' ? 'check_circle' : 
            appToast.type === 'error' ? 'error' : 
            appToast.type === 'warning' ? 'warning' : 'assignment_turned_in' 
          }}
        </span>
        <span>{{ appToast.message }}</span>
      </div>
    </transition>

  </div>
</template>

<script>
import loyaltyApi from '@/api/loyalty'; 

export default {
  name: 'Asofarma',
  data() {
    return {
      currentTab: 'alta',
      tarjetasEnPool: 0, 
      faltantes: 0,
      totalMeta: parseInt(process.env.VUE_APP_ASOFARMA_POOL_GOAL) || 150,
      isProcessingPost: false,
      
      currentPage: 1,
      lastPage: 1,
      totalPacientes: 0,
      itemsPerPage: 30, 
      
      pacientes: [],
      showModal: false,
      selectedRowIndex: null,
      
      modalData: {
        enableInput: false,
        cardNumber: '',
        isGenderNull: false,
        baseData: {},
        personal: {},
        medico: {},
        producto: {}
      },
      
      appToast: {
        show: false,
        message: '',
        type: 'info' 
      },
      toastTimeout: null, 
      
      directTab: { cardNumber: '', showSuccess: false },
      
      lockTimer: null,
      cachedToken: null, 
      loadingRowIndex: null
    };
  },
  computed: {
    colorFaltantes() {
      const porcentajePool = (this.tarjetasEnPool / this.totalMeta) * 100;
      if (porcentajePool <= 50) return '#F06261'; 
      else if (porcentajePool <= 75) return '#E5B800'; 
      else return 'var(--green)'; 
    },
    paginationText() {
      if (this.totalPacientes === 0) return 'Mostrando 0 resultados';
      const startText = (this.currentPage - 1) * this.itemsPerPage + 1;
      const endText = Math.min(this.currentPage * this.itemsPerPage, this.totalPacientes);
      return `Mostrando ${startText} a ${endText} de ${this.totalPacientes} resultados`;
    },
    isValidCard() {
      return this.modalData.cardNumber && this.modalData.cardNumber.length === 13;
    },
    isValidDirectCard() {
      return this.directTab.cardNumber && this.directTab.cardNumber.length === 13;
    },
    visiblePages() {
      const current = this.currentPage;
      const last = this.lastPage;
      if (last <= 7) {
        return Array.from({ length: last }, (_, i) => i + 1);
      }
      if (current <= 4) {
        return [1, 2, 3, 4, 5, '...', last];
      }
      if (current >= last - 3) {
        return [1, '...', last - 4, last - 3, last - 2, last - 1, last];
      }
      return [1, '...', current - 1, current, current + 1, '...', last];
    }
  },
  watch: {
    showModal(newVal) {
      document.body.style.overflow = newVal ? 'hidden' : '';
    }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.modalData.enableInput && this.modalData.barcode) {
      loyaltyApi.toggleCardLock(this.modalData.barcode, false);
      this.clearLockTimer();
    }
  },
  async mounted() {
    await this.fetchPoolSummary();
    await this.fetchPendingCards();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  methods: { 
    // INICIALIZACIÓN Y CONSUMO DE APIS 
    async fetchPoolSummary() {
      try {
        const data = await loyaltyApi.getCallcenterSummary(18);
        const summary = Array.isArray(data) && data.length > 0 ? data[0] : data;

        if (summary) {
          this.tarjetasEnPool = parseInt(summary.unused_cards) || 0;
          this.totalMeta = parseInt(process.env.VUE_APP_ASOFARMA_POOL_GOAL) || 150;
          this.faltantes = this.totalMeta - this.tarjetasEnPool;
          if (this.faltantes < 0) {
              this.faltantes = 0;
          }
        }
      } catch (error) {
        console.error("No se pudo cargar el resumen del pool:", error);
      }
    },

    async fetchPendingCards(page = 1) {
      try {
        const response = await loyaltyApi.getPendingCardsAsofarma(page);
        const rawData = response.data || []; 
        
        this.currentPage = response.current_page || 1;
        this.lastPage = response.last_page || 1;
        this.totalPacientes = response.total || 0;
        this.itemsPerPage = response.per_page || 30;
        
        this.pacientes = rawData.map(item => {
          const user = item.card?.user || {};
          const extra = user.extra_attr || {};
          return {
            id: item.id,
            barcode_virtual: item.barcode || '', 
            tarjeta_fisica: item.card?.barcode || '', 
            fecha_asignacion: item.created_at_,
            nombre: extra.names ? extra.names.join(' ') : (user.name || 'Sin Nombre'),
            apellido: extra.lastnames ? extra.lastnames.join(' ') : '',
            fecha_nacimiento: user.dob || 'No registrada',
            email: item.email_fake || user.email || '-------',
            status: 'pending',
            rawUser: user,
            rawExtra: extra,
            ticket: item.ticket || null,
            barcode: item.barcode, 
            rawOriginal: item,
            extra_attr: item.extra_attr
          };
        });
      } catch (error) {
        console.error("Error al cargar tarjetas pendientes:", error);
      }
    },
 
    // HELPERS Y EVALUADORES DE ESTADO 

    // Helper global para desempaquetar el JSON de forma segura una sola vez
    getSafeExtraAttr(paciente) {
      if (!paciente) return {};
      
      // Buscamos el extra_attr en las dos rutas posibles
      let extra = paciente.extra_attr || (paciente.rawOriginal ? paciente.rawOriginal.extra_attr : null);
      if (!extra) return {};

      try {
        // Desempaquetamos si viene como string múltiple (típico de MariaDB/Laravel)
        while (typeof extra === 'string') {
          extra = JSON.parse(extra);
        }
        return (typeof extra === 'object' && extra !== null && !Array.isArray(extra)) ? extra : {};
      } catch (e) {
        return {};
      }
    },

    isRecordProcessed(paciente) {
      const extra = this.getSafeExtraAttr(paciente);
      return !!extra.finalized_at; 
    },

    isRecordFailed(paciente) {
      const extra = this.getSafeExtraAttr(paciente);
      return !!extra.registration_failed; 
    },

    isRecordLocked(paciente) {
      const extra = this.getSafeExtraAttr(paciente);
      if (extra.lock_until) {
        const lockTime = new Date(extra.lock_until);
        return lockTime > new Date(); 
      }
      return extra.lock === true;
    },
 
    // GENERADORES DE FECHA Y CURP

    // Selecciona un rango basándose en su 'weight' (peso)
    weightedRandomTranche(tranches) {
      let totalWeight = 0;
      for (const tranche of tranches) {
        if (tranche.weight && tranche.weight > 0) {
          totalWeight += tranche.weight;
        }
      }

      if (totalWeight <= 0) {
        throw new Error('El peso total debe ser mayor a cero.');
      }

      const randomWeight = Math.floor(Math.random() * totalWeight) + 1;
      let runningWeight = 0;

      for (const tranche of tranches) {
        if (!tranche.weight || tranche.weight <= 0) continue;
        
        runningWeight += tranche.weight;
        if (randomWeight <= runningWeight) {
          return tranche;
        }
      }
      throw new Error('Fallo al seleccionar el rango de edad.');
    },

    // Genera la fecha sintética basada en la distribución
    generateFechaNacimiento(referenceDate = new Date()) {
      const ageTranches = [
        { min_age: 18, max_age: 24, weight: 3308 },
        { min_age: 25, max_age: 34, weight: 13231 },
        { min_age: 35, max_age: 44, weight: 15347 },
        { min_age: 45, max_age: 54, weight: 17444 },
        { min_age: 55, max_age: 64, weight: 14901 },
        { min_age: 65, max_age: 74, weight: 8594 },
        { min_age: 75, max_age: 84, weight: 3054 },
        { min_age: 85, max_age: 99, weight: 547 },
      ];

      const selectedTranche = this.weightedRandomTranche(ageTranches);
      const minAge = selectedTranche.min_age;
      const maxAge = selectedTranche.max_age;

      // Límite más antiguo (para maxAge)
      const earliestDob = new Date(referenceDate);
      earliestDob.setFullYear(earliestDob.getFullYear() - (maxAge + 1));
      earliestDob.setDate(earliestDob.getDate() + 1);

      // Límite más reciente (para minAge)
      const latestDob = new Date(referenceDate);
      latestDob.setFullYear(latestDob.getFullYear() - minAge);

      if (earliestDob > latestDob) {
        throw new Error('Rango de fechas inválido generado.');
      }

      // Diferencia en días y offset aleatorio
      const daysInRange = Math.floor((latestDob - earliestDob) / (1000 * 60 * 60 * 24));
      const randomOffset = Math.floor(Math.random() * (daysInRange + 1));

      const finalDate = new Date(earliestDob);
      finalDate.setDate(finalDate.getDate() + randomOffset);

      // Formatear a YYYY-MM-DD
      const year = finalDate.getFullYear();
      const month = String(finalDate.getMonth() + 1).padStart(2, '0');
      const day = String(finalDate.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    // Valida si la fecha entra en un rango "normal" (entre 0 y 100 años)
    isValidNormalDob(dobString) {
      if (!dobString || dobString === 'No registrada') return false;

      const dob = new Date(dobString);
      if (isNaN(dob.getTime())) return false;

      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();

      // Restar 1 si aún no ha cumplido años este año
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      // Retorna falso si es menor de edad (< 18) o tiene una edad anormalmente alta (> 100)
      if (age < 18 || age > 100) {
        return false;
      }

      return true;
    },

    // Sub-función extraída para limpiar openModal y manejar reglas demográficas
    inferirSexo(nombre, generoBD) {
      // Si la base de datos lo tiene, lo respetamos
      if (generoBD && generoBD.trim() !== '') {
        const s = generoBD.toUpperCase();
        return (s === 'M' || s.startsWith('MASC') || s.startsWith('H')) ? 'Masculino' : 'Femenino';
      }
      
      // Si no lo tiene, intentamos inferirlo por terminación y excepciones
      const primerNombre = (nombre || '').toUpperCase().split(' ')[0];
      const ultimaLetra = primerNombre.slice(-1);
      
      const excepcionesFemeninas = ['CARMEN', 'ISABEL', 'BEATRIZ', 'INES', 'GUADALUPE', 'DULCE', 'ROSARIO', 'IVONNE', 'LIZBETH'];
      const excepcionesMasculinas = ['JOSUE', 'LUCA', 'ANDREA', 'ELIAS'];
      
      if (excepcionesFemeninas.includes(primerNombre)) return 'Femenino';
      if (excepcionesMasculinas.includes(primerNombre)) return 'Masculino';
      
      return ultimaLetra === 'A' ? 'Femenino' : 'Masculino';
    },

    // Generador de CURP 
    generateCURP(nombre, apellido, dob, sexo) {
      const year = dob.substring(2, 4);
      const month = dob.substring(5, 7);
      const day = dob.substring(8, 10);
      const cleanStr = (str) => (str || '').toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Z ]/g, '').trim();
      const removeParticulas = (wordArray) => {
        const particulas = ['DA', 'DAS', 'DE', 'DEL', 'DER', 'DI', 'DIE', 'DD', 'EL', 'LA', 'LOS', 'LAS', 'LE', 'LES', 'MAC', 'MC', 'VAN', 'VON', 'Y'];
        return wordArray.filter(word => !particulas.includes(word));
      };
      const nombresArr = removeParticulas(cleanStr(nombre).split(' '));
      const apellidosArr = removeParticulas(cleanStr(apellido).split(' '));
      const paterno = apellidosArr[0] || 'XX';
      const materno = apellidosArr.length > 1 ? apellidosArr[1] : ''; 
      const primerNombre = nombresArr[0] || 'X';
      let nombreUsar = primerNombre;
      
      if (['JOSE', 'MARIA'].includes(primerNombre) && nombresArr.length > 1) {
        nombreUsar = nombresArr[1];
      }
      
      const getFirstInternalVowel = (word) => (word.substring(1).match(/[AEIOU]/) || ['X'])[0];
      const getFirstInternalConsonant = (word) => (word.substring(1).match(/[BCDFGHJKLMNPQRSTVWXYZ]/) || ['X'])[0];
      
      const letra1 = paterno.charAt(0);
      const letra2 = getFirstInternalVowel(paterno);
      const letra3 = materno ? materno.charAt(0) : 'X';
      const letra4 = nombreUsar.charAt(0);
      let cuatroLetras = `${letra1}${letra2}${letra3}${letra4}`;
      
      const malasPalabras = ['BACA','BAKA','BUEI','BUEY','CACA','CACO','CAGA','CAGO','CAKA','CAKO','COGE','COGI','COJA','COJE','COJI','COJO','COLA','CULO','FALO','FETO','GETA','GUEI','GUEY','JETA','JOTO','KACA','KACO','KAGA','KAGO','KAKA','KAKO','KOGE','KOGI','KOJA','KOJE','KOJI','KOJO','KOLA','KULO','LILO','LOCA','LOCO','LOKA','LOKO','MAME','MAMI','MEAR','MEAS','MEON','MIAR','MION','MOCO','MOKO','MULA','MULO','NACA','NACO','PEDA','PEDO','PENE','PIPI','PITO','POPO','PUTA','PUTO','QULO','RATA','ROBA','ROBE','ROBO','RUIN','SENO','TETA','VACA','VAGA','VAGO','VAKA','VUEI','VUEY','WUEI','WUEY'];
      
      if (malasPalabras.includes(cuatroLetras)) {
        cuatroLetras = `${letra1}X${letra3}${letra4}`;
      }
      
      let letraSexo = 'M'; 
      if (sexo && sexo.trim() !== '') {
        const s = sexo.toUpperCase();
        if (s === 'M' || s.startsWith('MASC') || s.startsWith('H')) letraSexo = 'H';
        if (s === 'F' || s.startsWith('FEM') || s.startsWith('MUJ')) letraSexo = 'M';
      }
      
      const entidades = ['AS', 'BC', 'BS', 'CC', 'CS', 'CH', 'DF', 'CL', 'CM', 'DG', 'GT', 'GR', 'HG', 'JC', 'MC', 'MN', 'MS', 'NT', 'NL', 'OC', 'PL', 'QT', 'QR', 'SP', 'SL', 'SR', 'TC', 'TS', 'TL', 'VZ', 'YN', 'ZS', 'NE'];
      const entidad = entidades[Math.floor(Math.random() * entidades.length)];
      
      const cons1 = getFirstInternalConsonant(paterno);
      const cons2 = materno ? getFirstInternalConsonant(materno) : 'X';
      const cons3 = getFirstInternalConsonant(nombreUsar);
      const tresConsonantes = `${cons1}${cons2}${cons3}`;
      
      const nacimientoAñoCompleto = parseInt(dob.substring(0, 4), 10);
      const char17 = nacimientoAñoCompleto < 2000 ? Math.floor(Math.random() * 10).toString() : ['A','B','C'][Math.floor(Math.random() * 3)];
      const char18 = Math.floor(Math.random() * 10).toString();
      const homoclave = `${char17}${char18}`;
      
      return `${cuatroLetras}${year}${month}${day}${letraSexo}${entidad}${tresConsonantes}${homoclave}`;
    },

    // Orquestador visual de Notificaciones No Bloqueantes
    showToast(message, type = 'info') {
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
      }
      this.appToast.message = message;
      this.appToast.type = type;
      this.appToast.show = true;
      this.toastTimeout = setTimeout(() => {
        this.appToast.show = false;
      }, 3500);
    },

    // FLUJO DEL MODAL Y VALIDACIÓN JUST-IN-TIME (JIT)

    // Sub-función extraída para manejar exclusivamente la validación Just-In-Time
    async checkJITStatus(paciente, index) {
      try {
        const statusData = await loyaltyApi.checkCardStatus(paciente.barcode.trim());
        
        if (statusData.is_finalized) {
          this.$set(this.pacientes, index, { ...paciente, status: 'activated', extra_attr: statusData.extra_attr });
          await this.fetchPoolSummary();
          this.showToast('Este registro ya fue dado de alta.', 'success');
          return true; // Bloqueamos la apertura del modal
        }
        
        if (statusData.is_failed) {
          this.$set(this.pacientes, index, { ...paciente, status: 'failed', extra_attr: statusData.extra_attr });
          await this.fetchPoolSummary();
          this.showToast('Este registro fue marcado como no válido.', 'error');
          return true; // Bloqueamos la apertura del modal
        }
      } catch (error) {
        console.warn("Validación JIT falló (Problema de red). Procediendo a abrir...", error);
      }
      return false; // Permitimos la apertura
    },

    async openModal(paciente, index) {
      this.loadingRowIndex = index;

      // Validación JIT delegada
      if (paciente.barcode && await this.checkJITStatus(paciente, index)) {
         this.loadingRowIndex = null;
         return; // Si el JIT devuelve true, cortamos la ejecución aquí
      }

      // Reseteo de Estado Base
      this.loadingRowIndex = null;
      this.selectedRowIndex = index;
      this.modalData.enableInput = false;
      this.modalData.cardNumber = '';
      this.modalData.barcode = paciente.barcode ? paciente.barcode.trim() : '';
      this.modalData.tarjeta_fisica = paciente.tarjeta_fisica;
      this.modalData.fecha_asignacion = paciente.fecha_asignacion;

      // Generación y Validación de la Fecha de Nacimiento
      let fechaNacimiento = paciente.fecha_nacimiento;
      if (!this.isValidNormalDob(fechaNacimiento)) {
        fechaNacimiento = this.generateFechaNacimiento();
      } 
      
      // Procesamiento del género (solo se toma si viene desde el endpoint)
      const dbGender = paciente.rawExtra.gender;
      this.modalData.isGenderNull = (!dbGender || dbGender.trim() === '');
      const sexoFormateado = this.inferirSexo(paciente.nombre, dbGender);
      const curpGenerada = this.generateCURP(paciente.nombre, paciente.apellido, fechaNacimiento, sexoFormateado);

      // Armado de Objetos Visuales para el Template
      this.modalData.baseData = { 
        nombre: paciente.nombre, 
        apellido: paciente.apellido, 
        dob: fechaNacimiento 
      };
      
      this.modalData.personal = {
        'Nombre': paciente.nombre,
        'Apellido(s)': paciente.apellido,
        'Fecha Nacimiento': fechaNacimiento,
        'CURP': curpGenerada,
        'Sexo': sexoFormateado,
        'Correo': paciente.email
      };
      
      // Mapeo inicial (skeleton loaders) mientras esperamos a MongoDB
      this.modalData.producto = { 'No. Ticket': '-------', 'Código de Barra': '-------', 'Marca del Producto': '-------' }; 
      this.modalData.medico = { 'Nombre': '-------', 'Cédula Profesional': '-------' };

      // Apertura y delegación de peticiones secundarias
      this.showModal = true;
      this.$nextTick(() => {
        this.fetchTransactionInfo(paciente);
      });
    },

    // ACTUALIZACIÓN DE ESTADO Y PETICIONES POST

    // Helper: Actualiza la tabla en vivo sin recargar la página (Reactividad Vue)
    updateLocalPatientState(index, status, extraFields) {
      if (index === null || !this.pacientes[index]) return;
      
      let pacienteActual = this.pacientes[index];
      let currentExtra = this.getSafeExtraAttr(pacienteActual);
      
      // Mezclamos el JSON viejo con los nuevos campos (ej. { finalized_at: '...' })
      const newExtra = { ...currentExtra, ...extraFields };
      
      // Le decimos a Vue que redibuje esta fila con el nuevo estado
      this.$set(this.pacientes, index, {
        ...pacienteActual,
        status: status,
        extra_attr: newExtra, 
        rawOriginal: {
          ...pacienteActual.rawOriginal,
          extra_attr: newExtra 
        }
      });
    },

    // El orquestador de guardado final
    async processRow(action) {
      if (action === 'activated') {
        const newBarcode = this.modalData.cardNumber;
        if (!newBarcode || newBarcode.length !== 13) return;
        
        this.isProcessingPost = true;
        try {
          // Guardar en Base de Datos
          await loyaltyApi.addVirtualCards([newBarcode], this.modalData.barcode); 
          
          // Actualizar Tabla Local y KPIs
          const firma = new Date().toISOString();
          this.updateLocalPatientState(this.selectedRowIndex, 'activated', { finalized_at: firma });
          await this.fetchPoolSummary();
          
          // Feedback visual
          this.showToast('¡Tarjeta agregada correctamente al Pool!', 'success');
          
          // Limpieza (Retraso ligero para que el usuario vea el éxito antes de cerrar)
          setTimeout(() => {
            this.modalData.cardNumber = '';
            this.modalData.barcode = null; 
            this.clearLockTimer();
            this.closeModal();
          }, 1000);

        } catch (error) {
          if (error.response && error.response.status === 409) {
            this.showToast('Esta tarjeta ya existe en el inventario.', 'warning');
          } else {
            console.error("Detalle del error del Backend en Modal:", error.response || error);
            this.showToast('Error al conectar con el servidor.', 'error');
          }
        } finally {
          this.isProcessingPost = false;
        }

      } else if (action === 'failed') {
        if (!this.modalData.barcode) return;
        
        this.isProcessingPost = true; 
        try { 
          // Marcar como fallido en Base de Datos
          await loyaltyApi.markCardAsFailed(this.modalData.barcode);
          
          // Actualizar Tabla Local
          this.updateLocalPatientState(this.selectedRowIndex, 'failed', { registration_failed: true });
          
          // Limpieza
          this.clearLockTimer();
          this.closeModal();
          this.showToast('El registro fue marcado como no válido.', 'error');

        } catch (error) {
          console.error("Error al marcar registro como fallido:", error);
          this.showToast('Hubo un problema al cancelar el registro.', 'error');
        } finally {
          this.isProcessingPost = false;
        }
      }
    },

    // Entrada directa de tarjetas 
    async submitDirectActivation() {
      const barcode = this.directTab.cardNumber;
      if (!barcode || barcode.length !== 13) return;
      
      this.isProcessingPost = true; 
      try {
        await loyaltyApi.addVirtualCards([barcode]);
        
        // Disparamos el Toast de éxito global
        this.showToast('¡Tarjeta agregada correctamente al Pool!', 'success');
        
        // Actualizamos los KPIs
        await this.fetchPoolSummary();
        
        // Limpiamos el input inmediatamente
        this.directTab.cardNumber = '';

      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.showToast('Esta tarjeta ya existe en el inventario.', 'warning');
        } else {
          console.error("Detalle del error del Backend:", error.response || error);
          this.showToast('Error al procesar la tarjeta. Revisa la consola.', 'error');
        }
      } finally {
        this.isProcessingPost = false; 
      }
    },

    // CONTROL DE CONCURRENCIA (LOCKS)

    async handleLockToggle() {
      const isLocked = this.modalData.enableInput;
      const barcode = this.modalData.barcode;
      
      if (!barcode) return;
      if (isLocked) {
        try {
          const statusData = await loyaltyApi.checkCardStatus(barcode.trim());
          
          if (statusData.is_finalized || statusData.is_failed) {
            this.modalData.enableInput = false;
            
            const newStatus = statusData.is_finalized ? 'activated' : 'failed';
            const msg = statusData.is_finalized 
              ? 'Este registro ya fue dado de alta por otro operador.' 
              : 'Este registro fue cancelado por otro operador.';
              
            this.updateLocalPatientState(this.selectedRowIndex, newStatus, statusData.extra_attr);
            
            this.showToast(msg, 'warning');
            this.closeModal(); 
            return; 
          }
        } catch (error) {
          console.warn("Fallo el JIT previo al lock, intentando bloquear de todos modos...", error);
        }
      }
      
      try {
        await loyaltyApi.toggleCardLock(barcode, isLocked);

        if (isLocked) {
          this.startLockTimer(barcode);
          this.cachedToken = await loyaltyApi.getAccessToken(); 
        } else {
          this.clearLockTimer();
        }
      } catch (error) {
        this.modalData.enableInput = !isLocked; 
        
        if (error.response && error.response.status === 409) { 
          this.showToast('Este registro lo está trabajando otro operador en este momento.', 'warning');
        } else {
          console.error("Error en el toggle de bloqueo:", error);
          this.showToast('Error de red al intentar bloquear el registro.', 'error');
        }
      }
    },

    startLockTimer(barcode) {
      this.clearLockTimer();
      // 600,000 ms = 10 minutos
      this.lockTimer = setTimeout(async () => {
        this.modalData.enableInput = false; 
        try {
          await loyaltyApi.toggleCardLock(barcode, false); 
        } catch (error) {
          console.error("Fallo la liberación por timer:", error);
        }
        
        // Reemplazamos el alert() por el Toast no bloqueante
        this.showToast("El tiempo de alta ha expirado. Registro liberado.", 'warning');
      }, 600000); 
    },

    clearLockTimer() {
      if (this.lockTimer) {
        clearTimeout(this.lockTimer);
        this.lockTimer = null;
      }
    },

    // Envía una petición de liberación al cerrar la pestaña del navegador (Beacon)
    handleBeforeUnload(event) {
      if (this.modalData.enableInput && this.modalData.barcode && this.cachedToken) {
        const url = `${process.env.VUE_APP_LOYALTY_ENDPOINT}api/callcenter/LockVc`;
        
        fetch(url, {
          method: 'POST',
          keepalive: true, // Esto asegura que la petición salga aunque la pestaña se cierre
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${this.cachedToken}`
          },
          body: JSON.stringify({
            virtualCard: this.modalData.barcode,
            lock: false 
          })
        });
      }
    },

    async closeModal() {
      if (this.modalData.enableInput && this.modalData.barcode) {
        try {
          await loyaltyApi.toggleCardLock(this.modalData.barcode, false);
          this.clearLockTimer();
        } catch (error) {
          console.error("Error al liberar la tarjeta al cerrar el modal", error);
        }
      }
      this.modalData.enableInput = false;
      this.showModal = false;
    },
 
    // UTILIDADES Y OTROS HELPERS 

    async fetchTransactionInfo(paciente) {
      if (!paciente.tarjeta_fisica || !paciente.fecha_asignacion) {
        this.modalData.producto = { 'No. Ticket': '-------', 'Código de Barra': '-------', 'Marca del Producto': '-------' };
        this.modalData.medico = { 'Nombre': '-------', 'Cédula Profesional': '-------' };
        return;
      }
      
      try {
        const transData = await loyaltyApi.getTransInfo(paciente.tarjeta_fisica, paciente.fecha_asignacion);
        
        let productosMostrados = { 'No. Ticket': transData.ticket || '-------' };
        
        const accumulate = transData.ticket_response?.balance?.productsAccumulate || [];
        const asoProducts = accumulate.filter(p => p.connectorId === 18);
        const ticketCatalog = transData.ticket_response?.balance?.ticket || [];
        
        if (asoProducts.length > 0) {
          asoProducts.forEach((prod, index) => {
            const sufijo = asoProducts.length > 1 ? ` ${index + 1}` : '';            
            productosMostrados[`Código de Barra${sufijo}`] = prod.barcode || '-------';
            const matchedProduct = ticketCatalog.find(t => t.barcode === prod.barcode);
            if (matchedProduct && matchedProduct.brand) {
              productosMostrados[`Marca${sufijo}`] = matchedProduct.brand;
            } else {
              productosMostrados[`Marca${sufijo}`] = 'No especificada';
            }
          });
        } else {
          productosMostrados['Código de Barra'] = '-------';
          productosMostrados['Marca del Producto'] = '-------';
        }
        
        // Vue actualizará el modal en vivo cambiando "Cargando..." por los datos reales
        this.modalData.producto = productosMostrados;
        
      } catch (error) {
        this.modalData.producto = { 'No. Ticket': '-------', 'Código de Barra': '-------', 'Marca del Producto': '-------' };
        this.modalData.medico = { 'Nombre': '-------', 'Cédula Profesional': '-------' };
      }
    },

    actualizarCurp() {
      if (!this.modalData.isGenderNull) return; 
      const { nombre, apellido, dob } = this.modalData.baseData;
      const nuevoSexo = this.modalData.personal['Sexo']; 
      
      this.modalData.personal['CURP'] = this.generateCURP(nombre, apellido, dob, nuevoSexo);
    },

    copyToClipboard(text) {
      const triggerSuccess = () => {
        this.showToast('Copiado en el portapapeles', 'info');
      };
      
      // API moderna (HTTPS requerido)
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
          .then(() => triggerSuccess())
          .catch(err => console.error('Error al copiar:', err));
      } else {
        // Fallback para navegadores antiguos o entornos no seguros
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed"; 
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus(); 
        textArea.select();
        
        try { 
          document.execCommand('copy'); 
          triggerSuccess(); 
        } catch (err) {
          console.error("Fallback de copiado falló", err);
        }
        
        document.body.removeChild(textArea);
      }
    },

    // Navegación de la tabla
    nextPage() { 
      if (this.currentPage < this.lastPage) this.fetchPendingCards(this.currentPage + 1); 
    },
    prevPage() { 
      if (this.currentPage > 1) this.fetchPendingCards(this.currentPage - 1); 
    },
    goToPage(page) { 
      if (page !== this.currentPage) this.fetchPendingCards(page); 
    }
  }
};
</script>

<style scoped>
/* ==========================================
  VARIABLES Y ANIMACIONES
 ========================================== */
.asofarma-dashboard {
  --blue: #40BAE5;
  --dark-green: #00837A;
  --gray: #9FA1A1;
  --green: #3EBFAC;
  --black: #000000;
  --white: #ffffff;
  font-family: 'trebuc', sans-serif;
  color: var(--black);
}

/* Animaciones Nativas Vue */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

.fade-modal-enter-active, .fade-modal-leave-active { transition: opacity 0.15s ease-out; }
.fade-modal-enter, .fade-modal-leave-to { opacity: 0; }
.fade-modal-enter .custom-modal-content { transform: scale(0.97); }
.fade-modal-enter-active .custom-modal-content { transition: transform 0.15s ease-out; }

/* ==========================================
   KPIS Y TOGGLE
   ========================================== */
.kpi-card { border: 1px solid #dcdcdc; border-radius: 12px; padding: 20px 40px; background-color: var(--white); min-width: 200px; }
.kpi-title { font-size: 16px; color: var(--gray); margin-bottom: 5px; font-weight: bold; }
.kpi-value { font-size: 42px; font-weight: bold; margin: 0; line-height: 1; transition: color 0.3s ease; }
.text-dark-green { color: var(--dark-green) !important; }

.custom-toggle { background-color: var(--white); border: 1px solid #dcdcdc; border-radius: 50px; width: 100%; max-width: 600px; }
.toggle-option { flex: 1; padding: 12px 20px; font-size: 15px; font-weight: bold; cursor: pointer; transition: all 0.3s ease; color: var(--black); border-radius: 50px; }
.toggle-option.active { background-color: var(--green); color: var(--white); }

.main-title { font-family: 'amaranth', sans-serif; font-weight: 700; font-size: 28px; color: var(--black); text-align: center; }

/* ==========================================
   TABLA
   ========================================== */
.custom-table-wrapper { width: 100%; max-width: 850px; margin: 0 auto; background: var(--white); }
.custom-table { border-collapse: separate; border-spacing: 0; width: 100%; }
.custom-table thead tr { background-color: var(--green); color: var(--white); }
.custom-table thead th { padding: 15px; font-weight: bold; border: none; }
.custom-table thead th:first-child { border-top-left-radius: 8px; }
.custom-table thead th:last-child { border-top-right-radius: 8px; }
.custom-table tbody td { border: none; font-size: 15px; }
.custom-table td, .table th { vertical-align: middle; }
.striped-row td { background-color: #f2f2f2; }

/* ==========================================
   PAGINACIÓN BOOTSTRAP
   ========================================== */
.custom-bs-pagination { gap: 5px; }
.custom-bs-pagination .page-link {
  width: 35px; height: 35px; border-radius: 4px !important;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--gray); color: var(--gray); font-weight: bold; transition: all 0.2s; background-color: var(--white);
}
.custom-bs-pagination .page-item.active .page-link { background-color: var(--green); border-color: var(--green); color: var(--white); }
.custom-bs-pagination .page-link:focus { outline: none; box-shadow: none; }
.custom-bs-pagination .page-item.disabled .page-link { background-color: #e0e0e0; border-color: #d0d0d0; color: #a0a0a0; cursor: not-allowed; }
.custom-bs-pagination .page-item:not(.active):not(.disabled) .page-link:hover { border-color: var(--green); color: var(--green); background-color: #f0fdfa; }

/* ==========================================
   BOTONES Y BADGES (Optimizados)
   ========================================== */
/* Propiedades compartidas para botones y badges */
.custom-btn-action, .custom-btn-error, .custom-btn-disabled, .badge-status {
  border-radius: 50px;
  padding: 6px 20px;
  font-weight: bold;
  font-size: 14px;
  border: none;
}

/* Acciones principales */
.custom-btn-action { background-color: var(--green); color: var(--white); transition: all 0.3s ease; }
.custom-btn-action:hover:not(:disabled) { background-color: var(--dark-green); color: var(--white); }

/* Acciones de error / Cancelar */
.custom-btn-error { background-color: #F06261; color: var(--white); transition: background-color 0.3s ease; }
.custom-btn-error:hover { background-color: #d8433e; color: var(--white); }

/* Estados deshabilitados unificados */
.custom-btn-disabled, .custom-btn-action:disabled {
  background-color: #d1d1d1 !important;
  border-color: #bcbcbc !important;
  color: #888888 !important;
  cursor: not-allowed;
  opacity: 0.8;
}

/* Badges de tabla */
.badge-status { display: inline-block; width: 160px; text-align: center; }
.badge-success { background-color: var(--gray); color: var(--white); }
.badge-error { background-color: #F06261; color: var(--white); }

/* ==========================================
   MODAL OVERLAY Y FORMULARIOS
   ========================================== */
.custom-modal-overlay { 
  position: fixed; top: 0; left: 0; right: 0; bottom: 0; 
  background-color: rgba(0, 0, 0, 0.65); 
  display: flex; align-items: center; justify-content: center; 
  z-index: 1050; 
}

.custom-modal-content { 
  background-color: var(--white); border-radius: 16px; padding: 40px; width: 90%; max-width: 800px; max-height: 90vh; 
  overflow-y: auto; position: relative; box-shadow: 0px 15px 40px rgba(0,0,0,0.25); scrollbar-width: none; -ms-overflow-style: none;
  text-align: left !important;
}
.custom-modal-content::-webkit-scrollbar { display: none; }

.close-btn { position: absolute; top: 20px; right: 20px; background: none; border: none; color: var(--gray); cursor: pointer; transition: color 0.2s; }
.close-btn:hover { color: var(--black); }

.modal-title { font-size: 26px; font-weight: bold; margin-bottom: 5px; }
.font-amaranth { font-family: 'amaranth', sans-serif; }
.modal-subtitle { color: var(--gray); font-size: 14px; margin-bottom: 20px; }
.text-green { color: var(--green); font-weight: bold; text-decoration: none; }

/* Checkbox Verde */
.custom-checkbox-label { cursor: pointer; user-select: none; }
.custom-checkbox { width: 22px; height: 22px; border: 2px solid var(--gray); border-radius: 4px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
.custom-checkbox.checked { background-color: var(--green); border-color: var(--green); color: var(--white); }

.section-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; margin-top: 10px; color: var(--black); }
.input-label { font-size: 13px; color: var(--gray); margin-bottom: 4px; }

.copy-input-group { position: relative; display: flex; align-items: center; }
.readonly-input { background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; font-weight: bold; color: var(--black); padding-right: 40px; transition: border-color 0.3s; }
.readonly-input:focus { border-color: var(--green); outline: none; box-shadow: 0 0 0 0.2rem rgba(62, 191, 172, 0.25); }

.copy-btn { position: absolute; right: 10px; background: none; border: none; color: var(--gray); cursor: pointer; transition: color 0.2s; }
.copy-btn:hover { color: var(--green); }

.custom-input { border: 1px solid var(--green); border-radius: 8px; padding: 10px; font-weight: bold; max-width: 400px; transition: all 0.3s; }
.custom-input:focus { outline: none; box-shadow: 0 0 0 0.25rem rgba(62, 191, 172, 0.25); }
.custom-input:disabled { border-color: #dee2e6; background-color: #e9ecef; }

.direct-activation-container { max-width: 500px; margin: 0 auto; width: 100%; }
.success-alert { border: 2px solid var(--green); border-radius: 8px; padding: 15px; display: inline-flex; align-items: center; background-color: #f0fdfa; }
.success-icon { background-color: var(--green); color: var(--white); border-radius: 50%; width: 30px; height: 30px; margin-right: 15px; }
.success-text { color: var(--dark-green); font-weight: bold; font-size: 15px; }

/* ==========================================
   SISTEMA DE TOASTS
   ========================================== */
.clipboard-toast {
  position: fixed;
  bottom: 20px;
  right: 20px;
  color: var(--white);
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  z-index: 1060;
  box-shadow: 0 8px 20px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Colores unificados con tu paleta */
.toast-success { background-color: var(--dark-green); } 
.toast-error { background-color: #F06261; }
.toast-warning { background-color: #E5B800; color: var(--black); } 
.toast-info { background-color: var(--gray); }

/* Transiciones */
.toast-enter-active, .toast-leave-active { transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1); }
.toast-enter, .toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>